import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function HandelswegeRoomShell({ visible }) {
  const { ParkettColor, ParkettNormal, ParkettRough, imperfectionRoughMap } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[4];
  const gltf = useGLTF("../models/Museum01/Handelswege.glb");

  const mats = useMemo(() => {
    ParkettColor.repeat.set(15, 15);
    ParkettNormal.repeat.set(15, 15);
    ParkettRough.repeat.set(15, 15);

    const m = {
      blackMattMaterial:      new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", roughnessMap: imperfectionRoughMap }),
      blackMatWallstMaterial:  new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#393939", roughnessMap: imperfectionRoughMap }),
      blackMatFloorMaterial:   new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
      blackTrans:              new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", transparent: true, opacity: 0.6 }),
      fullBlack:               new THREE.MeshStandardMaterial({ roughness: 1, color: "black" }),
      blackGlossyMaterial:     new THREE.MeshStandardMaterial({ roughness: 0.1, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      parkettMaterial:         new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0, map: ParkettColor, roughnessMap: ParkettRough, normalMap: ParkettNormal }),
      orangeGlossyWall:        new THREE.MeshStandardMaterial({ roughness: 0, metalness: 0, color: "#fc8207" }),
    };

    assignMaterialToObjects(gltf.scene, ["Parkett"], m.parkettMaterial);
    assignMaterialToObjects(gltf.scene, ["Decke", "Arrows"], m.blackMattMaterial);
    assignMaterialToObjects(gltf.scene, ["FLoor", "middleWallTop", "middleWall", "WegBlack", "podest"], m.blackMatFloorMaterial);
    assignMaterialToObjects(gltf.scene, ["Walls"], m.blackMatWallstMaterial);
    assignMaterialToObjects(gltf.scene, ["FullBlackWalls"], m.fullBlack);
    assignMaterialToObjects(gltf.scene, ["OrangeLines", "WegOrange", "PanoLines"], m.orangeGlossyWall);
    assignMaterialToObjects(gltf.scene, ["GloassyBlack"], m.blackGlossyMaterial);
    assignMaterialToObjects(gltf.scene, ["TextBackground"], m.blackTrans);

    return m;
  }, [gltf.scene]);

  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/Handelswege/LOW/"
      : "../overallTextures/Handelswege/";

    let active = true;
    const loadedTextures = [];

    const applyAO = (tex, ...targets) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      loadedTextures.push(tex);
      targets.forEach(m => { m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true; });
    };

    const loader = new THREE.TextureLoader();
    loader.load(base + "BakeHandelswegeWalls.webp", (tex) => applyAO(tex, mats.blackMatWallstMaterial));
    loader.load(base + "BakeHandelswegeFloor.webp", (tex) => applyAO(tex, mats.blackMatFloorMaterial, mats.parkettMaterial, mats.blackGlossyMaterial));

    return () => {
      active = false;
      loadedTextures.forEach(tex => tex.dispose());
      [mats.blackMatWallstMaterial, mats.blackMatFloorMaterial, mats.parkettMaterial, mats.blackGlossyMaterial].forEach(m => {
        m.aoMap = null; m.needsUpdate = true;
      });
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
